'use client'

import {
  useState,
  useId,
  useCallback,
  useEffect,
  type ChangeEvent,
  type ClipboardEvent,
  type FormEvent,
  type KeyboardEvent,
} from 'react'
import {
  Loader2,
  AlertCircle,
  Check,
  X,
  Mail,
  Building2,
  Users,
  Globe,
  MapPin,
  User,
  Phone,
} from 'lucide-react'
import { useRouter } from 'next/navigation'
import { useT } from '@/lib/useT'
import { useLanguage } from '@/app/LanguageContext'
import { ERP_LOGIN_URL } from '@/lib/erp'

// ─── Types ────────────────────────────────────────────────────
interface FormData {
  companyName: string
  contactName: string
  email: string
  phone: string
  city: string
  state: string
  country: string
  companySize: string
}

type FormErrors = Partial<Record<keyof FormData | 'submit', string>>

type VerificationStep = 'idle' | 'loading' | 'invalid' | 'expired' | 'success'

// ─── Static data ────────────────────────────────────────────
const COMPANY_SIZES = [
  { value: '1-10', label: '1-10 employees' },
  { value: '11-50', label: '11-50 employees' },
  { value: '51-200', label: '51-200 employees' },
  { value: '201-500', label: '201-500 employees' },
  { value: '501-1000', label: '501-1,000 employees' },
  { value: '1000Plus', label: '1,000+ employees' },
]

const COUNTRIES = [
  { value: 'IND', label: 'India' },
  { value: 'KSA', label: 'Kingdom of Saudi Arabia' },
  { value: 'UAE', label: 'United Arab Emirates' },
  { value: 'USA', label: 'United States of America' },
  { value: 'GBR', label: 'United Kingdom' },
]



// ─── FormField Component ───────────────────────────────────
interface FormFieldProps {
  label: string
  name: keyof FormData
  type?: 'text' | 'email' | 'tel' | 'select'
  placeholder?: string
  value: string
  error?: string
  onChange: (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void
  onBlur?: () => void
  required?: boolean
  icon?: React.ReactNode
  options?: Array<{ value: string; label: string }>
}

function FormField({
  label,
  name,
  type = 'text',
  placeholder,
  value,
  error,
  onChange,
  onBlur,
  required,
  icon,
  options,
}: FormFieldProps) {
  return (
    <div className="space-y-2">
      <label htmlFor={name} className="block text-sm font-medium text-[#0A171D]">
        {label}
        {required && <span className="text-[#E91E8C]">*</span>}
      </label>

      <div className="relative">
        {icon && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6C7377] pointer-events-none">
            {icon}
          </div>
        )}

        {type === 'select' ? (
          <select
            id={name}
            name={name}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            className={`w-full px-4 py-2.5 ${icon ? 'pl-11' : 'pl-4'} bg-white border rounded-sm transition-all duration-140 focus:outline-none focus:ring-2 focus:ring-[#FFC992] ${
              error
                ? 'border-[#E91E8C] focus:ring-[#E91E8C]'
                : 'border-[#C5C8C9] focus:border-[#003F47]'
            } text-[#0A171D] text-sm font-medium`}
          >
            <option value="">{placeholder || `Select ${label.toLowerCase()}`}</option>
            {options?.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        ) : (
          <input
            id={name}
            name={name}
            type={type}
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            className={`w-full px-4 py-2.5 ${icon ? 'pl-11' : 'pl-4'} bg-white border rounded-sm transition-all duration-140 focus:outline-none focus:ring-2 focus:ring-[#FFC992] ${
              error
                ? 'border-[#E91E8C] focus:ring-[#E91E8C]'
                : 'border-[#C5C8C9] focus:border-[#003F47]'
            } text-[#0A171D] text-sm font-medium placeholder:text-[#9EA2A5]`}
          />
        )}
      </div>

      {error && (
        <div className="flex items-start gap-2 text-[#E91E8C] text-xs mt-1">
          <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}
    </div>
  )
}

// ─── VerificationModal Component ───────────────────────────
interface VerificationModalProps {
  isOpen: boolean
  email: string
  onClose: () => void
  t: (key: string) => string
}

function VerificationModal({ isOpen, email, onClose, t }: VerificationModalProps) {
  const router = useRouter()
  const [code, setCode] = useState(['', '', '', '', ''])
  const [step, setStep] = useState<VerificationStep>('idle')
  const [secondsLeft, setSecondsLeft] = useState(60)
  const inputRefs = useId()

  const handleCodeInput = (index: number, value: string) => {
    const upperValue = value.toUpperCase().replace(/[^A-Z0-9]/g, '')
    if (upperValue.length > 1) return

    const newCode = [...code]
    newCode[index] = upperValue
    setCode(newCode)

    if (upperValue && index < 4) {
      const nextInput = document.getElementById(`code-${index + 1}`)
      nextInput?.focus()
    }
  }

  const handleCodeKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      const prevInput = document.getElementById(`code-${index - 1}`)
      prevInput?.focus()
    }
  }

  const handleCodePaste = (index: number, e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text').toUpperCase().replace(/[^A-Z0-9]/g, '')
    if (!pasted) return

    const newCode = [...code]
    let lastFilledIndex = index
    for (let i = 0; i < pasted.length && index + i < newCode.length; i++) {
      newCode[index + i] = pasted[i]
      lastFilledIndex = index + i
    }
    setCode(newCode)

    const nextEmptyIndex = Math.min(lastFilledIndex + 1, newCode.length - 1)
    const nextInput = document.getElementById(`code-${nextEmptyIndex}`)
    nextInput?.focus()
  }

  const handleVerify = async () => {
    setStep('loading')
    const otp = code.join('')

    try {
      const response = await fetch('/api/verify-otp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          otp,
        }),
      })

      if (!response.ok) {
        const error = await response.json()
        if (error.error.includes('expired')) {
          setStep('expired')
          setTimeout(() => setStep('idle'), 2000)
        } else {
          setStep('invalid')
          setTimeout(() => setStep('idle'), 2000)
        }
        return
      }

      setStep('success')
      setTimeout(() => {
        onClose()
        setCode(['', '', '', '', ''])
        setStep('idle')
        router.push(`/success?email=${encodeURIComponent(email)}`)
      }, 1500)
    } catch (error) {
      console.error('[v0] Verification error:', error)
      setStep('invalid')
      setTimeout(() => setStep('idle'), 2000)
    }
  }

  const handleResend = async () => {
    setStep('loading')
    try {
      const response = await fetch('/api/resend-otp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      })

      if (!response.ok) {
        throw new Error('Failed to resend OTP')
      }

      setSecondsLeft(600) // 10 minutes
      setStep('idle')
      setCode(['', '', '', '', ''])
    } catch (error) {
      console.error('[v0] Resend OTP error:', error)
      setStep('invalid')
      setTimeout(() => setStep('idle'), 2000)
    }
  }

  // Timer for resend countdown
  useEffect(() => {
    if (secondsLeft <= 0 || step === 'success' || !isOpen) return

    const timer = setTimeout(() => {
      setSecondsLeft((prev) => prev - 1)
    }, 1000)

    return () => clearTimeout(timer)
  }, [secondsLeft, step, isOpen])

  if (!isOpen) return null

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-40 bg-[#0A171D]/60 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-[18px] shadow-[0_8px_16px_rgba(10,23,29,0.08),0_32px_64px_rgba(10,23,29,0.16)] w-full max-w-[440px] p-8 animate-in fade-in zoom-in-95 duration-300">
          {step === 'success' ? (
            <div className="text-center">
              <div className="w-12 h-12 bg-[#EEF7F2] rounded-full flex items-center justify-center mx-auto mb-4">
                <Check size={24} className="text-[#003F47]" />
              </div>
              <h3 className="text-xl font-bold text-[#0A171D] mb-2">{t('signup.emailVerified')}</h3>
              <p className="text-[#6C7377] text-sm">
                {t('signup.accountCreated')}
              </p>
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h3 className="text-xl font-bold text-[#0A171D] mb-1">{t('signup.verifyEmail')}</h3>
                  <p className="text-[#6C7377] text-sm">
                    {t('signup.verifyEmailSent')}
                  </p>
                  <p className="text-[#0A171D] font-semibold text-sm mt-1 break-all">{email}</p>
                </div>
                <button
                  onClick={onClose}
                  className="text-[#6C7377] hover:text-[#0A171D] transition-colors"
                >
                  <X size={24} />
                </button>
              </div>

              {/* Code Input */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-[#0A171D] mb-3">
                  {t('signup.verificationCode')}
                </label>
                <div className="flex gap-2">
                  {code.map((digit, index) => (
                    <input
                      key={index}
                      id={`code-${index}`}
                      type="text"
                      inputMode="text"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleCodeInput(index, e.target.value)}
                      onKeyDown={(e) => handleCodeKeyDown(index, e)}
                      onPaste={(e) => handleCodePaste(index, e)}
                      className="w-12 h-12 text-center font-bold text-lg border-2 rounded-[8px] focus:outline-none transition-all duration-[220ms] bg-white text-[#0A171D]"
                      style={{
                        borderColor: step === 'invalid' ? '#E91E8C' : '#C5C8C9',
                      }}
                      disabled={step === 'loading'}
                    />
                  ))}
                </div>

                {step === 'invalid' && (
                  <div className="flex items-center gap-2 text-[#E91E8C] text-xs mt-2">
                    <AlertCircle size={14} />
                    <span>{t('signup.invalidCode')}</span>
                  </div>
                )}

                {step === 'expired' && (
                  <div className="flex items-center gap-2 text-[#E91E8C] text-xs mt-2">
                    <AlertCircle size={14} />
                    <span>{t('signup.expiredCode')}</span>
                  </div>
                )}
              </div>

              {/* Resend info */}
              <div className="text-center text-xs text-[#6C7377] mb-6">
                {secondsLeft > 0 ? (
                  <span>
                    {t('signup.resendCodeIn').replace(
                      '{seconds}',
                      `${Math.floor(secondsLeft / 60)}:${(secondsLeft % 60).toString().padStart(2, '0')}`
                    )}
                  </span>
                ) : (
                  <button
                    onClick={handleResend}
                    disabled={step === 'loading'}
                    className="text-[#003F47] hover:underline font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {step === 'loading' ? t('signup.sendingCode') : t('signup.resendCode')}
                  </button>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={onClose}
                  className="flex-1 px-4 py-2.5 border border-[#C5C8C9] rounded-[8px] text-[#0A171D] font-medium text-sm hover:bg-[#FFF9F2] transition-colors"
                >
                  {t('signup.cancelButton')}
                </button>
                <button
                  onClick={handleVerify}
                  disabled={step === 'loading' || code.join('').length !== 5}
                  className="flex-1 px-4 py-2.5 bg-[#FFBD76] rounded-[8px] text-[#0A171D] font-semibold text-sm hover:bg-[#F0A24E] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                >
                  {step === 'loading' ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      {t('signup.verifying')}
                    </>
                  ) : (
                    t('signup.verifyButton')
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  )
}

// ─── SignupSection Component ────────────────────────────────
export default function SignupSection() {
  const t = useT()
  const { language } = useLanguage()
  const [formData, setFormData] = useState<FormData>({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    city: '',
    state: '',
    country: '',
    companySize: '',
  })

  const [errors, setErrors] = useState<FormErrors>({})
  const [emailExists, setEmailExists] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [showVerification, setShowVerification] = useState(false)

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {}

    if (!formData.companyName.trim()) newErrors.companyName = t('signup.companyNameRequired')
    if (!formData.companySize) newErrors.companySize = t('signup.companySizeRequired')
    if (!formData.country) newErrors.country = t('signup.countryRequired')
    if (!formData.state.trim()) newErrors.state = t('signup.stateRequired')
    if (!formData.city.trim()) newErrors.city = t('signup.cityRequired')
    if (!formData.contactName.trim()) newErrors.contactName = t('signup.contactNameRequired')
    if (!formData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/))
      newErrors.email = t('signup.emailInvalid')
    else if (emailExists) newErrors.email = t('signup.emailExists')
    if (!formData.phone.trim()) newErrors.phone = t('signup.phoneRequired')

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  useEffect(() => {
    const email = formData.email
    if (!email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      setEmailExists(false)
      return
    }

    const timer = setTimeout(async () => {
      try {
        const response = await fetch('/api/check-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email }),
        })
        if (!response.ok) return
        const result = await response.json()
        setEmailExists(!!result.exists)
      } catch (error) {
        console.error('[v0] Check email error:', error)
      }
    }, 500)

    return () => clearTimeout(timer)
  }, [formData.email])

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    // Clear error for this field
    if (errors[name as keyof FormData]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }))
    }
  }

  const handleBlur = (field: keyof FormData) => {
    // Validate on blur
    if (field === 'email' && formData.email) {
      if (!formData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
        setErrors((prev) => ({ ...prev, email: 'Please enter a valid email' }))
      }
    }
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (!validateForm()) return

    setIsLoading(true)
    try {
      const payload = {
        companyName: formData.companyName,
        contactName: formData.contactName,
        email: formData.email,
        phone: formData.phone,
        city: formData.city,
        state: formData.state,
        country: formData.country,
        companySize: formData.companySize,
      }

      const response = await fetch('/api/signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      })

      if (!response.ok) {
        const error = await response.json()
        setErrors({ submit: error.error || 'Signup failed' })
        setIsLoading(false)
        return
      }

      const result = await response.json()
      setShowVerification(true)
    } catch (error) {
      console.error('[v0] Signup error:', error)
      setErrors({ submit: 'An error occurred. Please try again.' })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <section id="signup" className="bg-[#003F47] py-16 md:py-24">
      <div className="mx-auto max-w-[1200px] px-6">
        {/* Header */}
        <div className="mb-12 max-w-3xl">
          <span className="inline-block px-3 py-1.5 bg-white/15 text-[#FFBD76] rounded-[999px] text-xs font-semibold tracking-widest mb-4 uppercase">
            {t('signup.badge')}
          </span>
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="text-white">{t('signup.headline')} </span>
            <span className="text-[#FFBD76]">{t('signup.headlineGold')}</span>
          </h2>
          <p className="text-lg text-[#FFFBF7]/80 leading-relaxed">
            <span className="text-[#FFBD76]">{t('signup.descriptionGold1')}</span> No commitment. <span className="text-[#FFBD76]">{t('signup.descriptionGold2')}</span> {t('signup.descriptionEnd')}
          </p>
        </div>

        {/* Two-column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Benefits - left column */}
          <div className="hidden lg:block">
            <div className="sticky top-32 bg-white rounded-[18px] border border-[#E2E4E5] p-8 shadow-[0_1px_2px_rgba(10,23,29,0.06),0_2px_6px_rgba(10,23,29,0.06)]">
              <div className="flex flex-col gap-6">
                <div>
                  <h3 className="text-2xl font-bold text-[#0A171D] mb-2">{t('signup.trialBenefitsTitle')}</h3>
                  <p className="text-[#6C7377] text-sm leading-relaxed">
                    {t('signup.trialBenefitsDesc')}
                  </p>
                </div>

                <div className="space-y-3">
                  {['signup.benefit1', 'signup.benefit2', 'signup.benefit3', 'signup.benefit4', 'signup.benefit5', 'signup.benefit6'].map((key) => (
                    <div key={key} className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-0.5">
                        <Check size={20} className="text-[#003F47]" />
                      </div>
                      <span className="text-[#16242B] text-sm font-medium">{t(key as any)}</span>
                    </div>
                  ))}
                </div>

                <div className="pt-4 border-t border-[#E2E4E5]">
                  <div className="flex items-center gap-2 text-xs text-[#6C7377]">
                    <svg
                      className="w-4 h-4 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <span>
                      {t('signup.trustedBy')}{' '}
                      <strong className="text-[#0A171D]">{t('signup.enterpriseClients')}</strong> {t('signup.acrossMENA')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Form - spans 2 columns on desktop */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg border border-[#E2E4E5] p-8 shadow-sm">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Row 1 */}
                <FormField
                  label={t('signup.companyName')}
                  name="companyName"
                  placeholder={t('signup.placeholderCompany')}
                  value={formData.companyName}
                  error={errors.companyName}
                  onChange={handleChange}
                  onBlur={() => handleBlur('companyName')}
                  required
                  icon={<Building2 size={18} />}
                />

                {/* Row 2: Size and Country */}
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    label={t('signup.companySize')}
                    name="companySize"
                    type="select"
                    placeholder={t('signup.selectCompanySize')}
                    value={formData.companySize}
                    error={errors.companySize}
                    onChange={handleChange}
                    required
                    icon={<Users size={18} />}
                    options={COMPANY_SIZES.map(size => ({ ...size, label: t(`signup.${size.value}` as any) }))}
                  />
                  <FormField
                    label={t('signup.country')}
                    name="country"
                    type="select"
                    placeholder={t('signup.selectCountry')}
                    value={formData.country}
                    error={errors.country}
                    onChange={handleChange}
                    required
                    icon={<Globe size={18} />}
                    options={COUNTRIES}
                  />
                </div>

                {/* Row 3: State and City */}
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    label={t('signup.stateProvince')}
                    name="state"
                    placeholder={t('signup.placeholderState')}
                    value={formData.state}
                    error={errors.state}
                    onChange={handleChange}
                    onBlur={() => handleBlur('state')}
                    required
                    icon={<MapPin size={18} />}
                  />
                  <FormField
                    label={t('signup.city')}
                    name="city"
                    placeholder={t('signup.placeholderCity')}
                    value={formData.city}
                    error={errors.city}
                    onChange={handleChange}
                    onBlur={() => handleBlur('city')}
                    required
                    icon={<MapPin size={18} />}
                  />
                </div>

                {/* Row 4 */}
                <FormField
                  label={t('signup.contactPersonName')}
                  name="contactName"
                  placeholder={t('signup.placeholderContact')}
                  value={formData.contactName}
                  error={errors.contactName}
                  onChange={handleChange}
                  onBlur={() => handleBlur('contactName')}
                  required
                  icon={<User size={18} />}
                />

                {/* Row 5: Email and Phone */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <FormField
                      label={t('signup.businessEmail')}
                      name="email"
                      type="email"
                      placeholder={t('signup.placeholderEmail')}
                      value={formData.email}
                      error={errors.email}
                      onChange={handleChange}
                      onBlur={() => handleBlur('email')}
                      required
                      icon={<Mail size={18} />}
                    />
                    {emailExists && !errors.email && (
                      <div className="flex items-start gap-2 text-[#E91E8C] text-xs mt-1">
                        <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
                        <span>
                          {t('signup.emailExists')}{' '}
                          <a href={ERP_LOGIN_URL} className="underline font-semibold">
                            {t('signup.emailExistsLogin')}
                          </a>
                        </span>
                      </div>
                    )}
                  </div>
                  <FormField
                    label={t('signup.phoneNumber')}
                    name="phone"
                    type="tel"
                    placeholder={t('signup.placeholderPhone')}
                    value={formData.phone}
                    error={errors.phone}
                    onChange={handleChange}
                    onBlur={() => handleBlur('phone')}
                    required
                    icon={<Phone size={18} />}
                  />
                </div>

                {/* CTA */}
                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full px-6 py-3 bg-[#FFBD76] rounded-[8px] text-[#0A171D] font-bold text-base hover:bg-[#F0A24E] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 size={18} className="animate-spin" />
                        {t('signup.verifying')}
                      </>
                    ) : (
                      t('signup.createFreeAccount')
                    )}
                  </button>
                  <p className="text-xs text-[#9EA2A5] text-center mt-4">
                    {t('signup.footerText')}
                  </p>
                </div>


              </form>
            </div>
          </div>


        </div>
      </div>

      {/* Verification Modal */}
      <VerificationModal isOpen={showVerification} email={formData.email} onClose={() => setShowVerification(false)} t={t} />
    </section>
  )
}
